/** @jsxImportSource theme-ui */
import React from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import * as dto from 'client/dto';
import { LocationResiliencyToVulnerability, Vulnerability } from 'models/BepiModels';
import { countRiskFactors } from 'models/DisruptionModels';

import Grey from 'assets/PieIcons/InContainer/Grey.svg';
import Orange from 'assets/PieIcons/InContainer/Orange.svg';
import Red from 'assets/PieIcons/InContainer/Red.svg';

import { Typography } from '../../Typography';
import { BdNoDataIcon } from '../BdNoDataIcon';

const containerStyle: ThemeUIStyleObject = {
  position: 'relative',
  display: 'inlineBlock',
  width: '36px',
  height: '36px',
  margin: '0',
  padding: '0',
};
const imageStyle: ThemeUIStyleObject = {
  position: 'absolute',
  top: '0',
  left: '0',
};
const topRightStyle: ThemeUIStyleObject = {
  ...imageStyle,
  transform: 'rotate(90deg)',
};
const bottomLeftStyle: ThemeUIStyleObject = {
  ...imageStyle,
  transform: 'rotate(-90deg)',
};
const bottomRightStyle: ThemeUIStyleObject = {
  ...imageStyle,
  transform: 'rotate(-180deg)',
};

type BdDisruptionRiskIconProps = {
  disruption: dto.DisruptionModel;
  simple?: boolean;
  labelSmall?: boolean;
};
export const BdDisruptionRiskIcon: React.FC<BdDisruptionRiskIconProps> = ({
  disruption,
  simple = false,
  labelSmall = false,
}) => {
  const transmissibilityIcon = getTransmissibilityIcon(disruption?.transmissibility);
  const severityIcon = getSeverityIcon(disruption?.severity);
  const preventabilityIcon = getPreventabilityIcon(disruption?.preventability);
  const locationResiliencyIcon = getLocationResiliencyIcon(disruption?.locationResiliency);
  const riskFactors = countRiskFactors(disruption);
  const dataAvailable =
    transmissibilityIcon || severityIcon || preventabilityIcon || locationResiliencyIcon;
  const subtitle = dataAvailable
    ? riskFactors === 0
      ? 'No risk factors'
      : `${riskFactors}/4 risk factors`
    : 'No data available';

  return (
    <div sx={{ display: 'flex', alignItems: 'center' }}>
      <div id="container" sx={containerStyle}>
        {dataAvailable ? (
          <div>
            <img
              src={transmissibilityIcon}
              alt={transmissibilityIcon ? 'Transmissibility Icon' : null}
              sx={imageStyle}
            />
            <img
              src={preventabilityIcon}
              alt={preventabilityIcon ? 'Preventability Icon' : null}
              sx={topRightStyle}
            />
            <img
              src={severityIcon}
              alt={severityIcon ? 'Severity Icon' : null}
              sx={bottomLeftStyle}
            />
            <img
              src={locationResiliencyIcon}
              alt={locationResiliencyIcon ? 'Location Resiliency Icon' : null}
              sx={bottomRightStyle}
            />
          </div>
        ) : (
          <BdNoDataIcon />
        )}
      </div>
      {!simple && (
        <div
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ml: '8px',
          }}
        >
          <Typography variant="subtitle2" color="stone90" marginBottom="bottom">
            {subtitle}
          </Typography>
          <Typography
            variant={labelSmall ? 'label-small' : 'caption'}
            color={labelSmall ? 'stone50' : 'stone70'}
            marginBottom="0"
          >
            Disruptive potential
          </Typography>
        </div>
      )}
    </div>
  );
};

function getTransmissibilityIcon(
  transmissibility: dto.TransmissibilityDisruptionFactorType
): string {
  switch (transmissibility) {
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironmentLocally:
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment:
    case dto.TransmissibilityDisruptionFactorType.PossibleViaHumanToHuman:
      return Red;
    case dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally:
    case dto.TransmissibilityDisruptionFactorType.NotPossible:
    case dto.TransmissibilityDisruptionFactorType.Unlikely:
    case dto.TransmissibilityDisruptionFactorType.LimitedViaHumanToHuman:
      return Grey;
    case dto.TransmissibilityDisruptionFactorType.NoData:
      return null;
    default:
      return null;
  }
}

function getSeverityIcon(severity: dto.SeverityDisruptionFactorType): string {
  switch (severity) {
    case dto.SeverityDisruptionFactorType.Severe:
    case dto.SeverityDisruptionFactorType.ModerateToSevere:
      return Red;
    case dto.SeverityDisruptionFactorType.Moderate:
    case dto.SeverityDisruptionFactorType.MildToModerate:
      return Orange;
    case dto.SeverityDisruptionFactorType.Mild:
      return Grey;
    case dto.SeverityDisruptionFactorType.NoData:
      return null;
    default:
      return null;
  }
}

function getPreventabilityIcon(preventability: dto.PreventabilityDisruptionFactorType): string {
  switch (preventability) {
    case dto.PreventabilityDisruptionFactorType.NoVaccineOrProphylaxis:
      return Red;
    case dto.PreventabilityDisruptionFactorType.LimitedVaccine:
      return Orange;
    case dto.PreventabilityDisruptionFactorType.WideVaccine:
    case dto.PreventabilityDisruptionFactorType.WideProphylaxis:
      return Grey;
    case dto.PreventabilityDisruptionFactorType.NoData:
      return null;
    default:
      return null;
  }
}

function getLocationResiliencyIcon(locationResiliency?: number): string {
  const vulnerability: Vulnerability = LocationResiliencyToVulnerability(locationResiliency);
  switch (vulnerability) {
    case 'low':
      return Grey;
    case 'moderate':
      return Orange;
    case 'high':
      return Red;
    default:
      return null;
  }
}
